/** @jsxImportSource @emotion/react */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {
  Outlet,
  useMatch,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { css } from "@emotion/react";
import {
  Button,
  Column,
  ContextMenu,
  Icon,
  IconNames,
  Row,
  Separator,
  Text,
  TEXT_VARIANTS,
  ListItem,
} from "../components";
import { COLORS } from "../shared/theme";
import { useMediaQuery } from "react-responsive";
import { getAuth, signOut } from "firebase/auth";

export type TRootContext = {
  isNavigationExpanded: boolean;
  sidebarGap: number;
};

export const RootContext = React.createContext<TRootContext>({
  isNavigationExpanded: true,
  sidebarGap: 15,
});

const NavbarMenuItem: React.FC<{
  label: string;
  path: string;
}> = ({ label, path }) => {
  const matchPath = useMatch(path);
  const isMobile = useMediaQuery({ maxWidth: 700 });

  return (
    <Row
      as="li"
      mainAxisAlignment="center"
      crossAxisAlignment="center"
      css={css`
        height: 40px;
        flex-grow: 1;
        list-style: none;
      `}
    >
      <Link
        to={path}
        css={css`
          transition: all 120ms ease;
          display: flex;
          align-items: center;
          height: 100%;
          width: 100%;
          padding: 0 10px;
          line-height: 40px;
          text-align: left;
          text-decoration: none;
          color: ${matchPath !== null ? COLORS.BLACK : COLORS.SILVER};
          border-radius: 10px;
          background-color: ${COLORS.LIGHT};

          &:hover {
            color: ${COLORS.BLACK};
          }
        `}
      >
        <Text
          as="span"
          variant={isMobile ? TEXT_VARIANTS.T25 : TEXT_VARIANTS.T30}
        >
          {label}
        </Text>
      </Link>
    </Row>
  );
};

const MainNavigation: React.FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 700 });
  const navigate = useNavigate();

  const logout = useCallback(() => {
    const auth = getAuth();
    signOut(auth);
    navigate("/auth");
  }, [navigate]);

  return (
    <Row
      as="nav"
      mainAxisAlignment="center"
      crossAxisAlignment="center"
      css={css`
        width: 100%;
        height: 55px;
        background-color: ${COLORS.WHITE};
        box-shadow: 0 1px 5px rgba(0, 0, 0, 0.1);
      `}
    >
      <Row
        as="ul"
        gap={5}
        mainAxisAlignment="space-between"
        crossAxisAlignment="center"
        css={css`
          margin: 0;
          width: 100%;
          max-width: 1266px;
          padding: 0 ${isMobile ? 10 : 0}px;
        `}
      >
        <NavbarMenuItem label="Sublime" path="/" />
        <NavbarMenuItem label="Add" path="/add" />
        <NavbarMenuItem label="Profile" path="/profile" />
        <Link to="notifications">
          <Button
            variant="solid"
            leadingElement={() => <Icon name={IconNames.Notifications} />}
          />
        </Link>
        <ContextMenu
          renderElement={(toggleOpen, isOpen) => (
            <Button
              variant="solid"
              leadingElement={() => (
                <Icon
                  name={IconNames.Expand}
                  css={css`
                    fill: ${isOpen ? COLORS.BLACK : COLORS.SILVER};
                  `}
                />
              )}
              onClick={toggleOpen}
            />
          )}
          renderMenu={() => (
            <Column
              gap={5}
              css={css`
                width: 250px;
                padding: 5px;
              `}
            >
              <ListItem label="account@email.com" textColor={COLORS.SILVER} />
              <ListItem
                label="11 cards left on your trial"
                textColor={COLORS.SILVER}
              />
              <div
                css={css`
                  padding: 0 5px;
                `}
              >
                <Separator />
              </div>
              <ListItem
                textVariant={TEXT_VARIANTS.T20SEMIBOLD}
                label="Upgrade to Premium"
              />
              <Link to="/settings">
                <ListItem label="Settings" />
              </Link>
              <ListItem label="Log out" onClick={logout} />
              <div
                css={css`
                  padding: 0 5px;
                `}
              >
                <Separator />
              </div>
              {["About", "Help", "Newsletter", "Share feedback"].map(
                (label) => (
                  <ListItem label={label} />
                )
              )}
              <div
                css={css`
                  padding: 0 5px;
                `}
              >
                <Separator />
              </div>
              {["Terms of use", "Privacy policy"].map((label) => (
                <ListItem label={label} />
              ))}
            </Column>
          )}
        />
      </Row>
    </Row>
  );
};

const Root: React.FC = () => {
  const [isNavigationExpanded, setIsNavigationExpanded] = useState(true);
  const { navigationBehavior, sidebarGap } = useMemo(() => {
    const params = new URLSearchParams(window.location.href.split("?")[1]);

    return {
      navigationBehavior:
        params.has("n") && params.get("n") === "dynamic" ? "dynamic" : "sticky",
      sidebarGap: params.has("sgap")
        ? parseInt(params.get("sgap") as string)
        : 15,
    };
  }, []);
  const { pathname } = useLocation();

  useEffect(() => {
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
    });
  }, [pathname]);

  useLayoutEffect(() => {
    if (navigationBehavior === "sticky") return;

    let scrollY = window.scrollY;
    const handleScroll = () => {
      if (window.scrollY - scrollY > 8) {
        if (window.scrollY > 150) {
          setIsNavigationExpanded(false);
        }
      } else if (window.scrollY - scrollY < -8) {
        if (window.scrollY > 150) setIsNavigationExpanded(true);
      }
      scrollY = window.scrollY;
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [navigationBehavior]);

  return (
    <RootContext.Provider value={{ isNavigationExpanded, sidebarGap }}>
      <Column
        gap={50}
        crossAxisAlignment="center"
        css={css`
          padding-top: 105px;
        `}
      >
        <div
          css={css`
            transition: all 120ms ease;
            transform: translateY(${isNavigationExpanded ? 0 : -55}px);
            position: fixed;
            top: 0;
            z-index: 2;
            width: 100%;
          `}
        >
          <MainNavigation />
        </div>
        <Outlet />
      </Column>
    </RootContext.Provider>
  );
};

export default Root;
